"use client";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTransition } from "react";

type QPrimitive = string | number | boolean | null | undefined;
type QValue = QPrimitive | QPrimitive[];

type Options = {
  mode?: "replace" | "push";
  scroll?: boolean;
  reset?: true | string[];
};

export const useSetQuery = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const buildHref = (updates: Record<string, QValue>, opts?: Options) => {
    const params = new URLSearchParams(searchParams.toString());

    for (const [k, v] of Object.entries(updates)) {
      if (Array.isArray(v)) {
        params.delete(k);
        for (const item of v) {
          if (item == null || item === "") continue;
          params.append(k, String(item));
        }
      } else {
        if (v == null || v === "") params.delete(k);
        else params.set(k, String(v));
      }
    }

    if (opts?.reset === true) params.delete("page");
    else if (Array.isArray(opts?.reset)) for (const key of opts.reset) params.delete(key);

    const qs = params.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  };

  const setQuery = (updates: Record<string, QValue>, opts?: Options) => {
    const href = buildHref(updates, opts);
    const mode = opts?.mode ?? "replace";
    const scroll = opts?.scroll ?? false;

    startTransition(() => {
      if (mode === "push") {
        router.push(href, { scroll });
      } else {
        router.replace(href, { scroll });
      }
    });
  };

  return { setQuery, buildHref, isPending };
};
